import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import styled from "styled-components";
import { Link as RouterLink } from "../../../utils/router";
import { IComponent } from "../Component";

export const LINK_LIST = "link_list";

export interface Link {
  name: string;
  url: string;
}

export interface LinkListSpec {
  type: typeof LINK_LIST;
  links: Link[];
  vertical: boolean;
  dense: boolean;
}

export const Container = styled.div<{ vertical: boolean }>`
  display: flex;
  flex-wrap: wrap;
  ${(props) => (props.vertical ? "flex-flow: column;" : "")}
`;

export const LinkContainer = styled.div<{
  dense: boolean;
  isExternal: boolean;
}>`
  padding: ${(props) => (props.dense ? "2px 0" : "10px")};
  padding-left: ${(props) => (!props.isExternal && !props.dense ? "15px" : "")};
  padding-right: ${(props) =>
    !props.isExternal && !props.dense ? "15px" : ""};

  background: ${(props) =>
    props.dense ? "none" : !props.isExternal ? "#0d6efd" : "white"};
  ${(props) =>
    props.dense
      ? ""
      : !props.isExternal
      ? "box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25)"
      : "box-shadow: 0 1px 1px 0 rgba(60, 64, 67, 0.08), 0 1px 3px 1px rgba(60, 64, 67, 0.16)"};

  margin-top: ${(props) => (props.dense ? "5px" : "15px")};
  margin-right: ${(props) => (props.dense ? "10px" : "20px")};
  margin-bottom: ${(props) => (props.dense ? "0" : "5px")};
  text-decoration: none;
  color: ${(props) =>
    props.dense ? "#0d6efd" : !props.isExternal ? "white" : "inherit"};
  white-space: nowrap;
  text-overflow: ellipsis;
  border-radius: ${(props) => (!props.isExternal ? "0.375rem" : "3px")};
  max-width: fit-content;
  display: flex;
  align-items: center;
  font-weight: ${(props) => (!props.isExternal ? "500" : "normal")};

  /* Add Bootstrap btn style for internal links */
  ${(props) =>
    !props.isExternal && !props.dense
      ? `
    border: 1px solid #0d6efd;
    transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;
    
    &:hover {
      background-color: #0b5ed7;
      border-color: #0a58ca;
    }
    
    &:focus {
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }
  `
      : ""}
`;

export const NameContainer = styled.span`
  min-width: 30px;
  min-height: 12px;
  display: inline-block;
`;

export const LinkIcon = styled(FontAwesomeIcon).attrs({
  icon: faExternalLinkAlt,
})`
  margin-left: 8px;
  color: inherit;
  font-size: 0.85em;
`;

export const isExternalLink = (url: string): boolean => {
  return (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("mailto:") ||
    url.startsWith("tel:")
  );
};

const ExtLinkBase = (props: { spec: Link; dense: boolean }) => {
  const isExternal = isExternalLink(props.spec.url);

  return (
    <RouterLink href={props.spec.url}>
      <LinkContainer dense={props.dense} isExternal={isExternal}>
        <NameContainer>{props.spec.name}</NameContainer>
        {isExternal && <LinkIcon />}
      </LinkContainer>
    </RouterLink>
  );
};

const Component = (props: { spec: LinkListSpec }) => {
  return (
    <Container vertical={props.spec.vertical}>
      {props.spec.links.map((link, index) => (
        <ExtLinkBase key={index} spec={link} dense={props.spec.dense} />
      ))}
    </Container>
  );
};

export const LinkList: IComponent<LinkListSpec> = {
  component: Component,
};
